import { Box, Button, Flex, Image, Link, Text, useBreakpointValue } from "@chakra-ui/react";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";

const IMAGE_PARTS = 4;
const AUTOCHANGE_TIME = 5000;

const AnimatedCarousel = ({ slides, children }) => {
    const [activeSlide, setActiveSlide] = useState(0);
    const [prevSlide, setPrevSlide] = useState(-1);
    const [sliderReady, setSliderReady] = useState(false);
    const [opacityIcon, setOpacityIcon] = useState(0);
    const isGreaterThanMd = useBreakpointValue({ base: false, md: true });
    const changeTO = useRef(null);

    function RenderParagraph(props) {
        const title = props.title;
        const subtitle = props.subtitle;
        if (title && subtitle) {
            return (
                <Text
                    fontSize="lg"
                    align={{ md: "center", base: "center" }}
                    color="#FFCD00"
                    fontWeight="700"
                >
                    {title}
                    <Text
                        style={{ color: "white", fontWeight: "400" }}
                        fontSize="lg"
                        fontWeight={400}
                        display="inline-flex"
                        ml={1}
                    >
                        {subtitle}
                    </Text>
                </Text>
            );
        }
        return <></>;
    }

    // Auto-change timeout function
    const runAutochangeTO = () => {
        changeTO.current = setTimeout(() => {
            changeSlides(1);
        }, AUTOCHANGE_TIME);
    };

    useEffect(() => {
        // Run auto-change timeout on mount
        runAutochangeTO();

        // Set initial state
        setSliderReady(true);

        // Cleanup timeout on unmount
        return () => clearTimeout(changeTO.current);
    }, []);

    // Restart the timeout when activeSlide changes
    useEffect(() => {
        runAutochangeTO(); // Restart the timeout every time activeSlide changes

        // Reset opacity to 0 when changing slides
        setOpacityIcon(0);

        // Gradually increase opacity to 1 over time
        const intervalId = setInterval(() => {
            setOpacityIcon((prevOpacity) => {
                if (prevOpacity >= 100) {
                    clearInterval(intervalId); // Stop once the opacity reaches 100
                    return 4000;
                }
                return prevOpacity + 10; // Increment opacity by 10
            });
        }, 150); // Increment opacity every 150ms

        // Cleanup interval on unmount or when activeSlide changes
        return () => clearInterval(intervalId);
    }, [activeSlide]);

    const changeSlides = (change) => {
        clearTimeout(changeTO.current);
        const length = slides.length;
        const prev = activeSlide;
        let newActiveSlide = prev + change;

        if (newActiveSlide < 0) newActiveSlide = length - 1;
        if (newActiveSlide >= length) newActiveSlide = 0;

        setActiveSlide(newActiveSlide);
        setPrevSlide(prev);
    };

    return (
        <div className={classNames("slider", { "s--ready": sliderReady })}>
            {/* Search bar positioned above arrow buttons */}
            {children && (
                <Box
                    position="absolute"
                    top={{ base: "450px", md: "500px" }}
                    left="50%"
                    transform="translateX(-50%)"
                    zIndex={999}
                    width="100%"
                    maxWidth={{ base: "95vw", md: "70vw" }}
                    px={{ base: "2", md: "0" }}
                >
                    {children}
                </Box>
            )}

            <div className="slider__slides">
                {slides.map((slide, index) => {
                    return (
                        <div
                            className={classNames("slider__slide", {
                                "s--active": activeSlide === index,
                                "s--prev": prevSlide === index,
                            })}
                            key={slide.key}
                            cursor={slide.link ? "pointer" : "auto"}
                            onClick={() => {
                                if (slide.link) {
                                    window.open(slide.link);
                                }
                            }}
                        >
                            <div className="slider__slide-content">
                                {slide.icon && activeSlide === index && (
                                    <Image
                                        opacity={opacityIcon / 100}
                                        mb={5}
                                        src={slide.icon}
                                        alt={`Icon for ${slide.key}`}
                                        key={slide.key}
                                        width={{ base: "100px", md: "auto" }}
                                        style={{ height: "auto" }}
                                    />
                                )}
                                <h2 className="slider__slide-heading">
                                    {slide.text?.split(/(<br\s*\/?>)/i).map((part, i) =>
                                        part.match(/<br\s*\/?>/i) ? (
                                            // Si la parte es un <br />, renderiza un salto de línea real
                                            <br key={i} />
                                        ) : (
                                            // Si no es un <br />, renderiza el texto
                                            <span key={i}>{part}</span>
                                        )
                                    )}
                                </h2>
                                <p className="slider__slide-subheading">
                                    {slide.subtitle?.split(/(<br\s*\/?>)/i).map((part, i) =>
                                        part.match(/<br\s*\/?>/i) ? (
                                            // Si la parte es un <br />, renderiza un salto de línea real
                                            <br key={i} />
                                        ) : (
                                            // Si no es un <br />, renderiza el texto
                                            <span key={i}>{part}</span>
                                        )
                                    )}
                                </p>
                                <p className="slider__slide-subheading">
                                    {slide.subtitle2?.split(/(<br\s*\/?>)/i).map((part, i) =>
                                        part.match(/<br\s*\/?>/i) ? (
                                            // Si la parte es un <br />, renderiza un salto de línea real
                                            <br key={i} />
                                        ) : (
                                            // Si no es un <br />, renderiza el texto
                                            <span key={i}>{part}</span>
                                        )
                                    )}
                                </p>
                            </div>
                            {(slide.key === "hero2" &&
                                slide.key !== "guiarent" &&
                                slide.key === "hero") ||
                            (slide.key === "parqueferedalismo" && activeSlide === index) ? (
                                <Button
                                    opacity={opacityIcon / 100}
                                    w="280px"
                                    mb={4}
                                    py={6}
                                    px={2}
                                    rounded="full"
                                    color="#000"
                                    size="sm"
                                    right={0}
                                    zIndex={1000}
                                    _active={{ bg: "brand.500" }}
                                    aria-label="Comenzar requerimiento"
                                    bgColor="brand.500"
                                    _hover={{
                                        textDecoration: "none",
                                        color: "#000",
                                        bg: "brand.500",
                                    }}
                                    asChild
                                >
                                    <Link>Descubre más</Link>
                                </Button>
                            ) : (
                                <Box h="30%"></Box>
                            )}
                            {slide.key !== "guiarent" &&
                            slide.key !== "hero2" &&
                            slide.key !== "hero" &&
                            activeSlide === index ? (
                                <RenderParagraph
                                    title={slide.paragraph}
                                    subtitle={slide.paragraph1}
                                />
                            ) : null}
                            {slide.key === "guiarent" && activeSlide === index ? (
                                <Flex
                                    opacity={opacityIcon / 100}
                                    direction="column"
                                    w={isGreaterThanMd ? "100%" : "auto"}
                                    position="absolute"
                                    left={{ md: "0", base: "5" }}
                                    right="0"
                                    bottom="22%"
                                    alignItems="center"
                                    zIndex={1000}
                                >
                                    <Button
                                        w="280px"
                                        py={6}
                                        px={2}
                                        rounded="full"
                                        color="#000"
                                        size="sm"
                                        right={0}
                                        alignSelf={isGreaterThanMd ? "center" : "center"}
                                        _active={{ bg: "brand.500" }}
                                        aria-label="Comenzar requerimiento"
                                        bgColor="brand.500"
                                        _hover={{
                                            textDecoration: "none",
                                            color: "#000",
                                            bg: "brand.500",
                                        }}
                                        asChild
                                    >
                                        <Link>Empieza ahora</Link>
                                    </Button>
                                    <RenderParagraph
                                        title={slides[0].paragraph}
                                        subtitle={slides[0].paragraph1}
                                    />
                                </Flex>
                            ) : null}
                            <div className="slider__slide-parts">
                                {[...Array(IMAGE_PARTS)].map((_, i) => (
                                    <div className="slider__slide-part" key={i}>
                                        <div
                                            className="slider__slide-part-inner"
                                            style={{ backgroundImage: `url(${slide.imageUrl})` }}
                                        >
                                            {slide.imageUrl ? (
                                                <Image
                                                    src={slide.imageUrl}
                                                    alt={`Imagen de fondo del slide ${slide.key}`}
                                                    fill
                                                    style={{ objectFit: "cover" }}
                                                    display="none"
                                                />
                                            ) : (
                                                <p style={{ color: "white" }}>
                                                    Imagen no disponible
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="slider__control" onClick={() => changeSlides(-1)} />
            <div
                className="slider__control slider__control--right"
                onClick={() => changeSlides(1)}
            />
        </div>
    );
};

export default AnimatedCarousel;
